<template>
    <v-container fluid class="pt-0">
        <v-row>
            <v-col cols="12">
                <v-form>
                    <v-card>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <h3>{{ $t('settingsView.title') }}</h3>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <span>{{
                                        $t('settingsView.colorScheme')
                                    }}</span>
                                    <v-switch
                                        v-model="formData.isThemeDark"
                                        primary
                                        :label="
                                            formData.isThemeDark
                                                ? $t('settingsView.dark')
                                                : $t('settingsView.light')
                                        "
                                        :error-messages="isThemeDarkErrors"
                                        @input="
                                            $v.formData.isThemeDark.$touch()
                                        "
                                        @blur="clearServerErrors('isThemeDark')"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" xl="3">
                                    <span class="d-block mb-3">{{
                                        $t('settingsView.dateFormat')
                                    }}</span>
                                    <v-select
                                        v-model="formData.dateFormat"
                                        :items="dateFormatOptions"
                                        :label="$t('settingsView.dateFormat')"
                                        :error-messages="dateFormatErrors"
                                        @input="$v.formData.dateFormat.$touch()"
                                        @blur="clearServerErrors('dateFormat')"
                                        solo
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" xl="3">
                                    <span class="d-block mb-3">{{
                                        $t('settingsView.timeZone')
                                    }}</span>
                                    <v-autocomplete
                                        v-model="timeZone"
                                        :items="timeZoneOptions"
                                        :loading="isLoading"
                                        :label="$t('settingsView.timeZone')"
                                        :error-messages="timeZoneErrors"
                                        hide-no-data
                                        hide-selected
                                        return-object
                                        clearable
                                        :placeholder="
                                            $t('settingsView.searchTimeZone')
                                        "
                                        item-text="text"
                                        item-value="value"
                                        @update:search-input="
                                            onUpdateTimezoneSearch
                                        "
                                        @change="onChangeTimezone"
                                        @input="$v.formData.timeZone.$touch()"
                                        @blur="$v.formData.timeZone.$touch()"
                                        @keyup="clearServerErrors('timeZone')"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" xl="3">
                                    <span class="d-block mb-3">{{
                                        $t('settingsView.language')
                                    }}</span>
                                    <v-select
                                        v-model="formData.language"
                                        :items="languageOptions"
                                        :error-messages="languageErrors"
                                        :label="$t('settingsView.language')"
                                        @input="$v.formData.language.$touch()"
                                        @blur="clearServerErrors('language')"
                                        solo
                                    ></v-select>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn class="ma-2" depressed @click="onSave">{{
                                $t('settingsView.saveChanges')
                            }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-form>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';
import settingsValidatorMixin from '@/validators/settings/settingsValidator.mixin';

export default {
    name: 'settings',

    mixins: [settingsValidatorMixin],

    data() {
        const defaultFormData = {
            isThemeDark: false,
            dateFormat: null,
            timeZone: null,
            language: null
        };

        dayjs.extend(utc);
        dayjs.extend(timezone);
        dayjs.tz.setDefault(
            `(UTC) Europe, London ${dayjs().tz('Europe/London')}`
        );

        return {
            dateFormatOptions: ['MM/DD/YYYY', 'DD.MM.YYYY', 'DD-MM-YYYY'],
            searchedTimezones: [],
            languageOptions: [
                {
                    text: this.$t('settingsView.languages.english'),
                    value: 'en'
                },
                { text: this.$t('settingsView.languages.german'), value: 'de' }
            ],
            defaultFormData,
            timeZone: null,
            isLoading: false,
            formData: { ...defaultFormData }
        };
    },

    computed: {
        ...mapGetters({ settings: 'settings/getSettings' }),

        timeZoneOptions() {
            return this.searchedTimezones.map(timeZone => ({
                value: timeZone.value,
                text: `${timeZone.text} [${dayjs()
                    .tz(timeZone.value)
                    .format('HH:mm')}]`
            }));
        }
    },

    watch: {
        'formData.isThemeDark': {
            handler(value) {
                this.$vuetify.theme.dark = value;
            }
        },
        'formData.language': {
            handler(value) {
                this.$i18n.locale = value;
            }
        },
        settings(settings) {
            this.formData = { ...settings };
        }
    },

    async created() {
        if (!this.settings) {
            this.formData = { ...this.defaultFormData };

            return;
        }

        this.formData = { ...this.settings };

        const { timeZone } = this.settings;

        try {
            this.searchedTimezones = await this.searchTimezones(
                timeZone.split('/')[1]
            );

            this.timeZone = this.timeZoneOptions.find(
                ({ value }) => value === timeZone
            );
        } catch (err) {
            this.$notify({
                group: 'notify',
                type: 'error',
                title: 'Error',
                text: 'Unexpected error'
            });

            console.error(err);
        }
    },

    methods: {
        ...mapActions({
            saveSettings: 'settings/save',
            searchTimezones: 'settings/searchTimezones'
        }),

        async onSave() {
            try {
                this.$v.$touch();

                if (!this.isValid) {
                    return;
                }

                await this.saveSettings(this.formData);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('settingsView.editTitle'),
                    text: this.$t('settingsView.editSuccess')
                });
            } catch (error) {
                if (this.checkForServerFormErrors(error)) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.$t('settingsView.savedTitle'),
                        text: this.$t('settingsView.checkFormErrors')
                    });
                }

                console.error(error);
            }
        },

        onChangeTimezone(timeZone) {
            this.formData.timeZone = timeZone
                ? timeZone.value
                : this.defaultFormData.timeZone;
        },

        async onUpdateTimezoneSearch(query) {
            if (
                !query ||
                this.isLoading ||
                (this.timeZone && this.timeZone.text === query)
            ) {
                return;
            }

            this.isLoading = true;

            try {
                this.searchedTimezones = await this.searchTimezones(query);
            } catch (err) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: 'Error',
                    text: 'Unexpected error'
                });

                console.error(err);
            }

            this.isLoading = false;
        }
    }
};
</script>
